import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getSlugPrefix } from '@/utils/slug';
import './timeline.css';
import { DayPilot, DayPilotScheduler } from '@daypilot/daypilot-lite-react';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'components/atoms/index';
import { Input } from 'components/atoms/Input/Input';
import { academyStudentApi, academyLearningScheduleApi } from '@/services/api';
import { message, Modal, Checkbox } from 'antd';
import dayjs from 'dayjs';
import { useSessionSlug } from '@/hooks/useSessionSlug';

interface Student {
  id: string;
  name: string;
  birthDate: string;
  academy: string;
  loginId?: string;
}

interface LearningEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  endDate: string;
  resourceId: string;
  studentName: string;
  birthDate: string;
  backgroundColor?: string;
  borderColor?: string;
}

interface Resource {
  id: string;
  title: string;
  extendedProps: {
    birthDate: string;
    academy: string;
    loginId?: string;
    name?: string;
  };
}

const DEFAULT_EVENT_COLOR = '#4f46e5';

const parseColorToRGB = (color?: string): { r: number; g: number; b: number } | null => {
  if (!color) return null;
  const hexMatch = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let hexValue = hexMatch[1];
    if (hexValue.length === 3) {
      hexValue = hexValue.split('').map((char) => `${char}${char}`).join('');
    }
    const intValue = parseInt(hexValue, 16);
    return {
      r: (intValue >> 16) & 255,
      g: (intValue >> 8) & 255,
      b: intValue & 255,
    };
  }

  const rgbMatch = color.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  return null;
};

const getReadableFontColor = (color?: string): string | undefined => {
  const rgb = parseColorToRGB(color);
  if (!rgb) return undefined;
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness < 145 ? '#f8fafc' : '#0f172a';
};

const ROW_HEADER_MIN_WIDTH = 160;
const ROW_HEADER_MAX_WIDTH = 420;

const AcademyLearningManagement: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slugPrefix = getSlugPrefix(pathname);
  const { academyCode: sessionAcademyCode, status: sessionStatus } = useSessionSlug();
  const academyCode = sessionAcademyCode || '';
  const [events, setEvents] = useState<LearningEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<LearningEvent[]>([]);
  const rangeDefaults = useMemo(() => {
    const startOfMonth = dayjs().startOf('month');
    const endOfMonth = startOfMonth.endOf('month');
    return {
      cursor: startOfMonth.format('YYYY-MM-DD'),
      start: startOfMonth.format('YYYY-MM-DD'),
      end: endOfMonth.format('YYYY-MM-DD'),
    };
  }, []);
  const [rangeCursor, setRangeCursor] = useState<string>(rangeDefaults.cursor);
  const [rangeStart, setRangeStart] = useState<string>(rangeDefaults.start);
  const [rangeEnd, setRangeEnd] = useState<string>(rangeDefaults.end);
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [resources, setResources] = useState<Resource[]>([]);
  const [filteredResources, setFilteredResources] = useState<Resource[]>([]);
  // 복사 모달 상태
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyScheduleId, setCopyScheduleId] = useState<string | null>(null);
  const [copyCandidates, setCopyCandidates] = useState<Resource[]>([]);
  const [copySelected, setCopySelected] = useState<string[]>([]);
  const baselineEventsRef = useRef<Record<string, { start: string; endDate: string }>>({});
  const schedulerRef = useRef<DayPilot.Scheduler | null>(null);
  const schedulerContainerRef = useRef<HTMLDivElement | null>(null);
  const [pendingAdjustments, setPendingAdjustments] = useState<Record<string, { start?: string; endDate?: string }>>({});
  const [pendingCopies, setPendingCopies] = useState<{ sourceScheduleId: string; targetStudentUserIds: number[] }[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [rowHeaderWidth, setRowHeaderWidth] = useState<number>(260);
  const [isRowHeaderResizing, setIsRowHeaderResizing] = useState(false);
  const rowHeaderResizeSnapshot = useRef<{ startX: number; startWidth: number } | null>(null);
  const middlePanSnapshotRef = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
    schedulerScrollX: number;
    schedulerScrollY: number;
  } | null>(null);
  const [isMiddlePanning, setIsMiddlePanning] = useState(false);

  const pendingAdjustmentCount = Object.keys(pendingAdjustments).length;
  const pendingCopyCount = pendingCopies.reduce((sum, entry) => sum + entry.targetStudentUserIds.length, 0);
  const hasPendingChanges = pendingAdjustmentCount > 0 || pendingCopyCount > 0;
  const daysInRange = useMemo(
    () => Math.max(1, dayjs(rangeEnd).diff(rangeStart, 'day') + 1),
    [rangeStart, rangeEnd]
  );
  const todayRef = useMemo(() => dayjs().startOf('day'), []);
  const toSchedulerDate = useCallback((value: string) => (
    value.includes('T') ? value : `${value}T00:00:00`
  ), []);
  const overdueInfo = useMemo(() => {
    const eventMap: Record<string, boolean> = {};
    const resourceSet = new Set<string>();
    filteredEvents.forEach(ev => {
      const endDate = ev.endDate || ev.end || ev.start;
      if (!endDate) return;
      const isOverdue = dayjs(endDate).isBefore(todayRef, 'day');
      if (!isOverdue) return;
      eventMap[ev.id] = true;
      if (ev.resourceId) {
        resourceSet.add(String(ev.resourceId));
      }
    });
    return { eventMap, resourceSet };
  }, [filteredEvents, todayRef]);
  const schedulerEvents = useMemo<DayPilot.EventData[]>(() => (
    filteredEvents.map(ev => {
      const startIso = toSchedulerDate(ev.start);
      const exclusiveEnd = ev.end || dayjs(ev.endDate).add(1, 'day').format('YYYY-MM-DD');
      const endIso = toSchedulerDate(exclusiveEnd);
      const classNames = [];
      const isOverdue = overdueInfo.eventMap[ev.id];
      if (isOverdue) {
        classNames.push('scheduler-event-overdue');
      }
      const baseColor = ev.backgroundColor || DEFAULT_EVENT_COLOR;
      const backColor = isOverdue ? 'rgba(248, 113, 113, 0.28)' : baseColor;
      const borderColor = isOverdue ? 'transparent' : 'rgba(15, 23, 42, 0.08)';
      const fontColor = isOverdue ? '#7f1d1d' : getReadableFontColor(backColor);
      return {
        id: ev.id,
        text: ev.title,
        start: startIso,
        end: endIso,
        resource: ev.resourceId,
        backColor,
        borderColor,
        fontColor,
        cssClass: classNames.length ? classNames.join(' ') : undefined,
      };
    })
  ), [filteredEvents, overdueInfo, toSchedulerDate]);
  const schedulerResources = useMemo<DayPilot.ResourceData[]>(() => (
    filteredResources.map(res => ({
      id: res.id,
      name: res.title,
      tags: res.extendedProps,
      cssClass: overdueInfo.resourceSet.has(String(res.id)) ? 'scheduler-resource-overdue' : undefined,
    }))
  ), [filteredResources, overdueInfo]);
  const timeHeaders = useMemo<DayPilot.TimeHeaderData[]>(() => ([
    { groupBy: 'Month', format: viewMode === 'year' ? 'MMM' : 'MMMM' },
    { groupBy: 'Day', format: 'd' },
  ]), [viewMode]);
  const cellWidth = viewMode === 'year' ? 18 : 40;
  const rowHeaderWidthRounded = Math.round(rowHeaderWidth);

  const applyRange = useCallback((cursor: dayjs.Dayjs, mode: 'month' | 'year') => {
    const unit = mode === 'month' ? 'month' : 'year';
    const start = cursor.startOf(unit);
    const end = cursor.endOf(unit);
    const startStr = start.format('YYYY-MM-DD');
    const endStr = end.format('YYYY-MM-DD');
    setRangeCursor(startStr);
    setRangeStart(startStr);
    setRangeEnd(endStr);
  }, []);

  const handleShiftRange = useCallback((direction: number) => {
    setRangeCursor(prev => {
      const unit = viewMode === 'month' ? 'month' : 'year';
      const base = dayjs(prev).add(direction, unit);
      const start = base.startOf(unit);
      const end = base.endOf(unit);
      const startStr = start.format('YYYY-MM-DD');
      const endStr = end.format('YYYY-MM-DD');
      setRangeStart(startStr);
      setRangeEnd(endStr);
      return startStr;
    });
  }, [viewMode]);

  const scrollSchedulerBy = useCallback((direction: -1 | 1) => {
    const container = schedulerContainerRef.current;
    if (!container) return;
    const scheduler = schedulerRef.current;
    const stepSize = Math.max(120, Math.round(container.clientWidth * 0.6));
    const currentX = scheduler && typeof scheduler.getScrollX === 'function'
      ? scheduler.getScrollX()
      : container.scrollLeft;
    const nextX = Math.max(0, currentX + stepSize * direction);
    container.scrollLeft = nextX;
    if (scheduler && typeof scheduler.setScrollX === 'function') {
      scheduler.setScrollX(nextX);
    }
  }, []);

  const handleToday = useCallback(() => {
    applyRange(dayjs(), viewMode);
  }, [applyRange, viewMode]);

  const handleViewModeChange = useCallback((mode: 'month' | 'year') => {
    if (mode === viewMode) return;
    setViewMode(mode);
    applyRange(dayjs(rangeCursor), mode);
  }, [applyRange, rangeCursor, viewMode]);

  const isSchedulerBackgroundTarget = useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    if (target.closest('.scheduler-row-header-resizer')) return false;
    if (target.closest('.scheduler_default_event, .scheduler_default_event_inner')) return false;
    if (target.closest('.scheduler_default_rowheader, .scheduler_default_rowheader_inner')) return false;
    if (target.closest('button, a, input, textarea, select, [role="button"]')) return false;
    return Boolean(
      target.closest(
        '.scheduler_default_cell, .scheduler_default_cell_inner, .scheduler_default_timeheader, .scheduler_default_timeheader_cell, .scheduler_default_timeheader_cell_inner, .scheduler_default_main'
      )
    );
  }, []);

  const handleSchedulerContainerMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const isMiddleButton = event.button === 1;
    const isLeftButtonOnBackground = event.button === 0 && isSchedulerBackgroundTarget(event.target);
    if (!isMiddleButton && !isLeftButtonOnBackground) return;
    event.preventDefault();
    event.stopPropagation();
    const container = schedulerContainerRef.current;
    const scheduler = schedulerRef.current;
    if (!container || !scheduler) return;
    middlePanSnapshotRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: container.scrollLeft,
      scrollTop: container.scrollTop,
      schedulerScrollX: typeof scheduler.getScrollX === 'function' ? scheduler.getScrollX() : 0,
      schedulerScrollY: typeof scheduler.getScrollY === 'function' ? scheduler.getScrollY() : 0,
    };
    setIsMiddlePanning(true);
  }, [isSchedulerBackgroundTarget]);

  const handleRowHeaderResizeStart = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    rowHeaderResizeSnapshot.current = {
      startX: event.clientX,
      startWidth: rowHeaderWidthRounded,
    };
    setIsRowHeaderResizing(true);
  }, [rowHeaderWidthRounded]);

  useEffect(() => {
    if (!isRowHeaderResizing) return;

    const handleMouseMove = (event: MouseEvent) => {
      if (!rowHeaderResizeSnapshot.current) return;
      const delta = event.clientX - rowHeaderResizeSnapshot.current.startX;
      const nextWidth = Math.min(
        ROW_HEADER_MAX_WIDTH,
        Math.max(
          ROW_HEADER_MIN_WIDTH,
          rowHeaderResizeSnapshot.current.startWidth + delta
        )
      );
      setRowHeaderWidth(nextWidth);
    };

    const handleMouseUp = () => {
      setIsRowHeaderResizing(false);
      rowHeaderResizeSnapshot.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isRowHeaderResizing]);

  useEffect(() => {
    if (!isMiddlePanning) return;
    const handleMouseMove = (event: MouseEvent) => {
      const container = schedulerContainerRef.current;
      const scheduler = schedulerRef.current;
      const snapshot = middlePanSnapshotRef.current;
      if (!snapshot) return;
      if (!container) {
        setIsMiddlePanning(false);
        middlePanSnapshotRef.current = null;
        return;
      }
      const deltaX = event.clientX - snapshot.startX;
      const deltaY = event.clientY - snapshot.startY;

      if (container) {
        container.scrollLeft = snapshot.scrollLeft - deltaX;
        container.scrollTop = snapshot.scrollTop - deltaY;
      }

      if (scheduler) {
        const nextScrollX = Math.max(0, (snapshot.schedulerScrollX ?? 0) - deltaX);
        const nextScrollY = Math.max(0, (snapshot.schedulerScrollY ?? 0) - deltaY);
        if (typeof scheduler.setScrollX === 'function') {
          scheduler.setScrollX(nextScrollX);
        }
        if (typeof scheduler.setScrollY === 'function') {
          scheduler.setScrollY(nextScrollY);
        }
      }
    };
    const handleMouseUp = () => {
      setIsMiddlePanning(false);
      middlePanSnapshotRef.current = null;
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMiddlePanning]);

  useEffect(() => {
    if (!isRowHeaderResizing && !isMiddlePanning) return;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    const activeCursor = isRowHeaderResizing ? 'col-resize' : 'grabbing';
    document.body.style.cursor = activeCursor;
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
    };
  }, [isRowHeaderResizing, isMiddlePanning]);

  // 리소스 데이터 생성
  const generateResources = (studentList: Student[]): Resource[] => {
    return studentList.map(student => ({
      id: student.id,
      title: `${student.name} / ${student.loginId || student.id}`, // [QA Fix] 학생 ID 노출
      extendedProps: {
        birthDate: student.birthDate,
        academy: student.academy,
        loginId: student.loginId || student.id,
        name: student.name,
      }
    }));
  };

  // 학생 목록 로드
  useEffect(() => {
    if (sessionStatus !== 'ready' || !academyCode) return;
    (async () => {
      try {
        let merged: Student[] = [];
        let page = 0;
        const size = 200;
        let more = true;
        
        while (more) {
          const res = await academyStudentApi.list({ academyCode, page, size });
          const data = res?.data || res;
          const pageData = data?.data || data;
          const content = pageData?.content || [];
          const items: Student[] = content.map((s: any) => ({
            id: String(s.userId),
            name: s.name || s.displayName || s.studentName,
            birthDate: s.birthDate || s.birth || '',
            academy: s.academyName || academyCode,
            loginId: s.studentLoginId || s.loginId || String(s.userId),
          }));
          merged = merged.concat(items);
          const total = pageData?.totalElements ?? content.length;
          page++;
          if (merged.length >= total || content.length < size) more = false;
        }
        
        // 중복 제거
        const dedupMap = new Map<string, Student>();
        for (const s of merged) {
          const key = `${s.id}`;
          if (!dedupMap.has(key)) dedupMap.set(key, s);
        }
        
        const initialResources = generateResources(Array.from(dedupMap.values()));
        setResources(initialResources);
        setFilteredResources(initialResources);
      } catch (e) {
        message.error('학생 목록을 불러오지 못했습니다.');
      }
    })();
  }, [academyCode, sessionStatus]);

  const [searchDraft, setSearchDraft] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const applySearch = () => setSearchKeyword(searchDraft.trim());
  const clearSearch = () => {
    setSearchDraft('');
    setSearchKeyword('');
  };

  // 리소스 및 이벤트 필터링
  useEffect(() => {
    const tokens = searchKeyword
      .toLowerCase()
      .split(/\s+/)
      .filter(Boolean);

    if (!tokens.length) {
      setFilteredResources(resources);
      const allowedIds = new Set(resources.map(r => r.id));
      setFilteredEvents(events.filter(e => allowedIds.has(e.resourceId)));
      return;
    }

    const matchResource = (resource: Resource) => {
      const haystacks = [
        (resource.title || '').toLowerCase(),
        String(resource.id || '').toLowerCase(),
        (resource.extendedProps?.academy || '').toLowerCase(),
        (resource.extendedProps?.loginId || '').toLowerCase(),
        (resource.extendedProps?.name || '').toLowerCase(),
      ];
      return tokens.every(token => haystacks.some(text => text.includes(token)));
    };

    const matched = resources.filter(matchResource);
    setFilteredResources(matched);
    const allowedIds = new Set(matched.map(r => r.id));
    setFilteredEvents(events.filter(e => allowedIds.has(e.resourceId)));
  }, [resources, events, searchKeyword]);

  const loadSchedules = useCallback(async () => {
    try {
      const res = await academyLearningScheduleApi.list({ start: rangeStart, end: rangeEnd });
      const data = res?.data || res;
      const payload = data?.events || data?.data?.events || [];
      const mapped: LearningEvent[] = payload.map((ev: any) => {
        const fallbackMoment = dayjs();
        const startMoment = dayjs(ev.start);
        const resolvedStartMoment = startMoment.isValid() ? startMoment : fallbackMoment;
        const startDate = resolvedStartMoment.format('YYYY-MM-DD');
        const endMoment = dayjs(ev.end || ev.start);
        const resolvedEndMoment = endMoment.isValid() ? endMoment : resolvedStartMoment;
        const inclusiveEnd = resolvedEndMoment.format('YYYY-MM-DD');
        const exclusiveEnd = resolvedEndMoment.add(1, 'day').format('YYYY-MM-DD');
        return {
          id: String(ev.id),
          title: ev.title || '학습',
          start: startDate,
          end: exclusiveEnd,
          endDate: inclusiveEnd,
          resourceId: String(ev.studentUserId ?? ev.resourceId ?? ''),
          studentName: '',
          birthDate: '',
          backgroundColor: ev.backgroundColor || '#1890ff',
          borderColor: ev.borderColor || ev.backgroundColor || '#1890ff'
        };
      });
      
      // 리소스와 매칭되지 않는 이벤트용 리소스 보강
      const missingIds = new Set<string>();
      mapped.forEach(ev => {
        if (!resources.find(r => r.id === ev.resourceId)) missingIds.add(ev.resourceId);
      });
      
      if (missingIds.size > 0 && academyCode) {
        const extras: Resource[] = [];
        
        for (const id of missingIds) {
          try {
            const res = await academyStudentApi.list({ academyCode, page: 0, size: 1000 });
            const data = res?.data || res;
            const pageData = data?.data || data;
            const content = pageData?.content || [];
            const student = content.find((s: any) => String(s.userId) === String(id));
            
            if (student) {
              extras.push({
                id,
                title: `${student.name || student.displayName || student.studentName} / ${student.birthDate || ''}`,
                extendedProps: { birthDate: student.birthDate || '', academy: academyCode }
              });
            } else {
              extras.push({
                id,
                title: `학생 ID: ${id} / `,
                extendedProps: { birthDate: '', academy: academyCode }
              });
            }
          } catch (_) {
            extras.push({
              id,
              title: `학생 ID: ${id} / `,
              extendedProps: { birthDate: '', academy: academyCode }
            });
          }
        }
        
        setResources(prev => {
          const exist = new Set(prev.map(r => r.id));
          const dedup = extras.filter(r => !exist.has(r.id));
          return dedup.length ? [...prev, ...dedup] : prev;
        });
      }
      
      setEvents(mapped);
      baselineEventsRef.current = mapped.reduce<Record<string, { start: string; endDate: string }>>((acc, ev) => {
        acc[ev.id] = { start: ev.start, endDate: ev.endDate };
        return acc;
      }, {});
      setPendingAdjustments({});
      setPendingCopies([]);
    } catch (e) {
      // silent fail
    }
  }, [academyCode, rangeStart, rangeEnd, resources]);

  useEffect(() => {
    loadSchedules();
  }, [loadSchedules]);

  const markEventDirty = useCallback((eventId: string, nextValues: { start?: string; endDate?: string }) => {
    setPendingAdjustments(prev => {
      const baseline = baselineEventsRef.current[eventId];
      if (!baseline) return prev;
      const updated = { ...prev };
      const existing = { ...(updated[eventId] || {}) };

      if (nextValues.start !== undefined) {
        if (nextValues.start !== baseline.start) {
          existing.start = nextValues.start;
        } else {
          delete existing.start;
        }
      }

      if (nextValues.endDate !== undefined) {
        if (nextValues.endDate !== baseline.endDate) {
          existing.endDate = nextValues.endDate;
        } else {
          delete existing.endDate;
        }
      }

      if (!Object.keys(existing).length) {
        delete updated[eventId];
      } else {
        updated[eventId] = existing;
      }

      return updated;
    });
  }, []);

  const openCopyModalForEvent = useCallback(async (eventId: string) => {
    try {
      // 복사 대상자 필터링 제거 (모든 학생 표시)
      const allowed: Resource[] = filteredResources;
      if (!allowed.length) {
        message.info('복사할 수 있는 대상 학생이 없습니다.');
        return;
      }
      setCopyCandidates(allowed);
      setCopySelected([]);
      setCopyScheduleId(eventId);
      setCopyModalVisible(true);
    } catch (_) {
      message.error('복사 대상 조회 중 오류가 발생했습니다.');
    }
  }, [filteredResources]);

  const handleEventMove = useCallback((args: DayPilot.SchedulerEventMoveArgs) => {
    const eventId = String(args.e.id());
    if (args.newResource && args.newResource !== args.e.resource()) {
      message.warning('다른 학생으로 이동은 불가합니다. 복사 기능을 이용하세요.');
      args.preventDefault();
      return;
    }
    // [Modified] Check against original start date from baseline to allow free movement before saving
    const baseline = baselineEventsRef.current[eventId];
    const originalStart = baseline ? dayjs(baseline.start).startOf('day') : dayjs(args.e.start().toString()).startOf('day');
    const newStart = dayjs(args.newStart.toString()).startOf('day');

    if (newStart.isBefore(originalStart)) {
      message.warning('원래 시작일보다 이전으로 변경할 수 없습니다.');
      args.preventDefault();
      return;
    }
    const exclusiveEndInstant = args.newEnd?.toString();
    if (!exclusiveEndInstant) {
      args.preventDefault();
      return;
    }
    const exclusiveEndDate = dayjs(exclusiveEndInstant).format('YYYY-MM-DD');
    const newEndDate = dayjs(exclusiveEndInstant).subtract(1, 'day').format('YYYY-MM-DD');
    const newStartStr = newStart.format('YYYY-MM-DD');
    setEvents(prev => prev.map(ev => ev.id === eventId
      ? { ...ev, start: newStartStr, end: exclusiveEndDate, endDate: newEndDate }
      : ev));
    markEventDirty(eventId, { start: newStartStr, endDate: newEndDate });
    message.success('일정 이동이 저장 대기 상태입니다. 캘린더 저장 버튼을 눌러 반영하세요.');
  }, [markEventDirty]);

  const handleEventResize = useCallback((args: DayPilot.SchedulerEventResizeArgs) => {
    const eventId = String(args.e.id());
    
    // 시작일 변경 감지
    const oldStart = dayjs(args.e.start().toString()).startOf('day');
    const newStart = dayjs(args.newStart.toString()).startOf('day');
    const isStartChanged = !newStart.isSame(oldStart);

    if (isStartChanged) {
      const baseline = baselineEventsRef.current[eventId];
      const originalStart = baseline ? dayjs(baseline.start).startOf('day') : oldStart;
      if (newStart.isBefore(originalStart)) {
        message.warning('원래 시작일보다 이전으로 변경할 수 없습니다.');
        args.preventDefault();
        return;
      }
    }

    const exclusiveEndInstant = args.newEnd?.toString();
    if (!exclusiveEndInstant) {
      args.preventDefault();
      return;
    }
    const exclusiveEndDate = dayjs(exclusiveEndInstant).format('YYYY-MM-DD');
    const newEndDate = dayjs(exclusiveEndInstant).subtract(1, 'day');
    
    // 시작일이 종료일보다 늦어지는 경우 방지 (최소 1일 유지)
    if (newEndDate.isBefore(newStart)) {
      message.warning('종료일은 시작일 이전일 수 없습니다.');
      args.preventDefault();
      return;
    }

    const newStartStr = newStart.format('YYYY-MM-DD');
    const newEndDateStr = newEndDate.format('YYYY-MM-DD');

    setEvents(prev => prev.map(ev => ev.id === eventId
      ? { ...ev, start: newStartStr, end: exclusiveEndDate, endDate: newEndDateStr }
      : ev));

    const dirtyUpdates: { start?: string; endDate?: string } = { endDate: newEndDateStr };
    if (isStartChanged) {
      dirtyUpdates.start = newStartStr;
    }
    markEventDirty(eventId, dirtyUpdates);
    
    message.success('일정 기간 변경이 저장 대기 상태입니다. 캘린더 저장 버튼을 눌러 반영하세요.');
  }, [markEventDirty]);

  const handleEventClick = useCallback((args: DayPilot.SchedulerEventClickArgs) => {
    navigate(`${slugPrefix}/academy/learning/detail/${args.e.id()}`);
  }, [navigate, slugPrefix]);

  const handleEventRightClick = useCallback((args: DayPilot.SchedulerEventRightClickArgs) => {
    args.preventDefault();
    openCopyModalForEvent(String(args.e.id()));
  }, [openCopyModalForEvent]);

  const handleBeforeRowHeaderRender = useCallback((args: DayPilot.SchedulerBeforeRowHeaderRenderArgs) => {
    if (args.row.index % 2 === 0) {
      args.row.backColor = '#F3F4F6';
    } else {
      args.row.backColor = '#FFFFFF';
    }
  }, []);

  const handleBeforeCellRender = useCallback((args: DayPilot.SchedulerBeforeCellRenderArgs) => {
    const rowIndex = args.cell.row?.index ?? 0;
    const cssTokens: string[] = [];
    if (rowIndex % 2 === 0) {
      args.cell.properties.backColor = '#F9FAFB';
    } else {
      args.cell.properties.backColor = '#FFFFFF';
    }

    const cellDate = dayjs(args.cell.start ? args.cell.start.toDate() : undefined);
    if (cellDate.isValid()) {
      const isWeekend = [0, 6].includes(cellDate.day());
      const isToday = cellDate.isSame(todayRef, 'day');
      const isMonthBoundary = cellDate.date() === 1;

      if (isWeekend) {
        cssTokens.push('scheduler-cell-weekend');
      }
      if (isMonthBoundary) {
        cssTokens.push('scheduler-cell-month-boundary');
      }
      if (isToday) {
        cssTokens.push('scheduler-cell-today');
      }
    }

    if (cssTokens.length) {
      const existing = args.cell.properties.cssClass;
      args.cell.properties.cssClass = existing
        ? `${existing} ${cssTokens.join(' ')}`
        : cssTokens.join(' ');
    }
  }, [todayRef]);

  const handleBeforeTimeHeaderRender = useCallback((args: DayPilot.SchedulerBeforeTimeHeaderRenderArgs) => {
    const headerLevel = args.header.level ?? 0;
    const headerDate = dayjs(args.header.start ? args.header.start.toDate() : undefined);

    if (headerLevel === 0) {
      args.header.html = headerDate.isValid()
        ? `<div class="scheduler-header-month">${headerDate.format('YYYY년 M월')}</div>`
        : args.header.html;
      return;
    }

    if (!headerDate.isValid()) return;

    const cssClasses = ['scheduler-header-day'];
    const weekday = headerDate.day();
    const isWeekend = weekday === 0 || weekday === 6;
    const isToday = headerDate.isSame(todayRef, 'day');

    if (isWeekend) {
      cssClasses.push('scheduler-header-day--weekend');
    }
    if (isToday) {
      cssClasses.push('scheduler-header-day--today');
    }

    args.header.html = `
      <div class="${cssClasses.join(' ')}">
        <span class="scheduler-header-day__date">${headerDate.format('D')}</span>
      </div>
    `;
  }, [todayRef]);

  const handleSaveChanges = useCallback(async () => {
    if (!hasPendingChanges) {
      message.info('저장할 변경이 없습니다.');
      return;
    }
    setIsSaving(true);
    try {
      for (const [eventId, change] of Object.entries(pendingAdjustments)) {
        const baseline = baselineEventsRef.current[eventId];
        if (!baseline) continue;
        if (change.start && change.start !== baseline.start) {
          const deltaDays = dayjs(change.start).diff(dayjs(baseline.start), 'day');
          if (deltaDays !== 0) {
            await academyLearningScheduleApi.adjust(eventId, { mode: 'drag', deltaDays });
          }
        }
        if (change.endDate && change.endDate !== baseline.endDate) {
          await academyLearningScheduleApi.adjust(eventId, { mode: 'resize', newEndDate: change.endDate });
        }
      }

      for (const copy of pendingCopies) {
        if (!copy.targetStudentUserIds.length) continue;
        await academyLearningScheduleApi.copyToStudents(copy.sourceScheduleId, { targetStudentUserIds: copy.targetStudentUserIds });
      }

      message.success('캘린더 변경 사항이 저장되었습니다.');
      await loadSchedules();
    } catch (error) {
      console.error('캘린더 저장 실패:', error);
      message.error('캘린더 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  }, [hasPendingChanges, pendingAdjustments, pendingCopies, loadSchedules]);

  // 복사 모달 핸들러
  const handleCopyOk = () => {
    if (!copyScheduleId || copySelected.length === 0) {
      message.warning('숙제 부여할 학생 ID를 입력해주세요.'); // [QA Fix] 복사 대상 미선택 안내
      return;
    }

    const uniqueTargets = Array.from(
      new Set(
        copySelected
          .map(id => Number(id))
          .filter(id => !Number.isNaN(id))
      )
    );

    if (uniqueTargets.length === 0) {
      message.warning('복사할 학생을 다시 선택해 주세요.');
      return;
    }

    setPendingCopies(prev => {
      const next = [...prev];
      const idx = next.findIndex(item => item.sourceScheduleId === copyScheduleId);
      if (idx >= 0) {
        const merged = new Set(next[idx].targetStudentUserIds);
        uniqueTargets.forEach(id => merged.add(id));
        next[idx] = { ...next[idx], targetStudentUserIds: Array.from(merged) };
      } else {
        next.push({ sourceScheduleId: copyScheduleId, targetStudentUserIds: uniqueTargets });
      }
      return next;
    });

    message.success('복사 요청이 저장 대기 상태로 추가되었습니다. 캘린더 저장 버튼을 눌러 반영하세요.');
    
    setCopyModalVisible(false);
    setCopyScheduleId(null);
    setCopyCandidates([]);
    setCopySelected([]);
  };

  const handleCopyCancel = () => {
    setCopyModalVisible(false);
    setCopyScheduleId(null);
    setCopyCandidates([]);
    setCopySelected([]);
  };


  return (
    <main className='flex flex-1 pt-8 w-full md:py-5 md:bg-transparent'>
      <div className='flex-1 w-full'>
        <div className='flex justify-between items-center mb-5'>
          <h1 className='pb-3 heading1'>학습 배분</h1>
          <div className='flex gap-3 items-center'>
            {/* {hasPendingChanges && (
              <span className='text-sm text-gray-500'>
                저장 대기:
                {pendingAdjustmentCount > 0 && ` 일정 조정 ${pendingAdjustmentCount}건`}
                {pendingAdjustmentCount > 0 && pendingCopyCount > 0 && ' ·'}
                {pendingCopyCount > 0 && ` 복사 ${pendingCopyCount}명`}
              </span>
            )} */}

          </div>
        </div>

<div className='flex gap-2 justify-between items-center mb-4'>
        <section className='flex flex-wrap gap-2 items-center mb-4 w-full'>
          <Input
            placeholder='학생 이름, ID 또는 학원명으로 검색'
            className='max-w-[360px]'
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applySearch();
              }
            }}
          />
          <Button onClick={applySearch}>검색</Button>
          <Button intent='secondary' onClick={clearSearch} disabled={!searchKeyword}>
            초기화
          </Button>
        </section>
        <div className='flex flex-none gap-2 items-center'>
          <Button
                className='w-32'
                disabled={!hasPendingChanges || isSaving}
                onClick={handleSaveChanges}
              >
                {isSaving ? '저장 중...' : '캘린더 저장'}
              </Button>
              <Button onClick={() => navigate(`${slugPrefix}/academy/learning/assign/detail`)}>
                학습 / 숙제 배분
              </Button>
        </div>
</div>

        {/* 총 학생 수 표시
        <div style={{ marginBottom: '16px', textAlign: 'right' }}>
          <span style={{ color: '#666', fontSize: '14px' }}>
            총 {filteredResources.length}명의 학생
          </span>
        </div> */}

        <section className='flex flex-col flex-1 gap-5'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex gap-2'>
              <Button onClick={() => handleShiftRange(-1)}>이전</Button>
              <Button onClick={handleToday}>오늘</Button>
              <Button onClick={() => handleShiftRange(1)}>다음</Button>
            </div>
            <div className='text-sm text-gray-500'>
              {dayjs(rangeStart).format('YYYY.MM.DD')} ~ {dayjs(rangeEnd).format('YYYY.MM.DD')}
            </div>
            <div className='flex items-center gap-2'>
              <Button onClick={() => handleViewModeChange('month')} disabled={viewMode === 'month'}>
                월 단위
              </Button>
              <Button onClick={() => handleViewModeChange('year')} disabled={viewMode === 'year'}>
                연 단위
              </Button>
              <Button
                aria-label="캘린더 왼쪽 이동"
                title="왼쪽으로 이동"
                onClick={() => scrollSchedulerBy(-1)}
              >
                <LeftOutlined />
              </Button>
              <Button
                aria-label="캘린더 오른쪽 이동"
                title="오른쪽으로 이동"
                onClick={() => scrollSchedulerBy(1)}
              >
                <RightOutlined />
              </Button>
            </div>
          </div>
          <div
            ref={schedulerContainerRef}
            className='learning-scheduler-wrapper relative h-[600px] w-full overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-md'
            onMouseDown={handleSchedulerContainerMouseDown}
          >
            <DayPilotScheduler
              controlRef={schedulerRef}
              startDate={rangeStart}
              days={daysInRange}
              scale="Day"
              cellWidth={cellWidth}
              timeHeaders={timeHeaders}
              resources={schedulerResources}
              events={schedulerEvents}
              rowHeaderWidth={rowHeaderWidthRounded}
              heightSpec="Fixed"
              height={600}
              durationBarVisible={false}
              locale="ko-kr"
              theme="scheduler_default"
              eventMoveHandling="Update"
              eventResizeHandling="Update"
              eventRightClickHandling="Enabled"
              eventClickHandling="Enabled"
              eventDeleteHandling="Disabled"
              timeRangeSelectedHandling="Disabled"
              onEventMove={handleEventMove}
              onEventResize={handleEventResize}
              onEventClick={handleEventClick}
              onEventRightClick={handleEventRightClick}
              onBeforeRowHeaderRender={handleBeforeRowHeaderRender}
              onBeforeCellRender={handleBeforeCellRender}
              onBeforeTimeHeaderRender={handleBeforeTimeHeaderRender}
            />
            <div
              className={`scheduler-row-header-resizer ${isRowHeaderResizing ? 'scheduler-row-header-resizer--active' : ''}`}
              style={{ left: `${rowHeaderWidthRounded}px` }}
              onMouseDown={handleRowHeaderResizeStart}
              role="presentation"
              aria-hidden="true"
            >
              <span />
            </div>
          </div>
        </section>
      </div>

      {/* 복사 모달 */}
      <Modal
        title="학습 복사"
        open={copyModalVisible}
        onOk={handleCopyOk}
        onCancel={handleCopyCancel}
        width={600}
        okText="복사"
        cancelText="취소"
      >
        <div style={{ marginBottom: '16px' }}>
          <p>아래 학생들에게 학습을 복사합니다. 복사할 학생을 선택하세요.</p>
        </div>
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {copyCandidates.map((resource) => (
            <div key={resource.id} style={{ marginBottom: '8px' }}>
              <Checkbox
                checked={copySelected.includes(resource.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setCopySelected([...copySelected, resource.id]);
                  } else {
                    setCopySelected(copySelected.filter(id => id !== resource.id));
                  }
                }}
              >
                {resource.title}
              </Checkbox>
            </div>
          ))}
        </div>
        {copyCandidates.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
            복사할 수 있는 학생이 없습니다.
          </div>
        )}
        <p className='mt-4 text-sm text-gray-500'>
          선택한 학생들은 상단의 캘린더 저장 버튼을 눌렀을 때 실제로 복사됩니다.
        </p>
      </Modal>
    </main>
  );
};

export default AcademyLearningManagement; 
